import { defineComponent as _defineComponent } from 'vue';
import { BAlert, BButtonGroup, BCol, BContainer, BRow } from "bootstrap-vue";
import { computed, ref, watch } from "vue";
import {} from "vue";
import { GalaxyApi } from "@/api";
import { getAppRoot } from "@/onload/loadConfig";
import { errorMessageAsString } from "@/utils/simple-error";
import { capitalizeFirstLetter } from "@/utils/strings";
import LoadingSpan from "../LoadingSpan.vue";
import HelpText from "@/components/Help/HelpText.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'WorkflowInvocationMetrics',
    props: {
        invocationId: null,
        notTerminal: { type: Boolean }
    },
    setup(__props) {
        const props = __props;
        const VegaWrapper = () => import("./VegaWrapper.vue");
        const groupBy = ref("tool_id");
        const timing = ref("seconds");
        const jobMetrics = ref();
        const fetchError = ref();
        const attributeToLabel = {
            tool_id: "Tool ID",
            step_id: "Step",
        };
        async function fetchMetrics() {
            const { data, error } = await GalaxyApi().GET("/api/invocations/{invocation_id}/metrics", {
                params: {
                    path: {
                        invocation_id: props.invocationId,
                    },
                },
            });
            if (error) {
                fetchError.value = errorMessageAsString(error);
            }
            else {
                jobMetrics.value = data;
            }
        }
        watch(() => props.invocationId, () => fetchMetrics(), { immediate: true });
        function itemToX(item) {
            if (groupBy.value === "tool_id") {
                return item.tool_id;
            }
            else if (groupBy.value === "step_id") {
                return `${item.step_index + 1}: ${item.step_label || item.tool_id}`;
            }
            else {
                throw Error("Cannot happen");
            }
        }
        function computeAllocatedCoreTime(jobMetrics) {
            const walltimePerJob = {};
            const coresPerJob = {};
            const jobInfo = {};
            jobMetrics?.forEach((jobMetric) => {
                if (jobMetric.name == "runtime_seconds") {
                    walltimePerJob[jobMetric.job_id] = parseFloat(jobMetric.raw_value);
                    jobInfo[jobMetric.job_id] = {
                        toolId: jobMetric.tool_id,
                        stepIndex: jobMetric.step_index,
                        stepLabel: jobMetric.step_label,
                    };
                }
                else if (jobMetric.name == "galaxy_slots") {
                    coresPerJob[jobMetric.job_id] = parseFloat(jobMetric.raw_value);
                }
            });
            const thisMetric = [];
            Object.keys(walltimePerJob).map((jobId) => {
                const walltime = walltimePerJob[jobId];
                const cores = coresPerJob[jobId];
                const thisJobInfo = jobInfo[jobId];
                if (cores && thisJobInfo && walltime) {
                    const metric = {
                        name: "allocated_core_time",
                        job_id: jobId,
                        raw_value: (cores * walltime).toString(),
                        tool_id: thisJobInfo.toolId,
                        step_index: thisJobInfo.stepIndex,
                        step_label: thisJobInfo.stepLabel,
                    };
                    thisMetric.push(metric);
                }
            });
            return thisMetric;
        }
        function metricToSpecData(jobMetrics, metricName, yTitle, helpTerm, transform) {
            const thisMetric = jobMetrics?.filter((jobMetric) => jobMetric.name == metricName);
            const values = thisMetric?.map((item) => {
                let y = parseFloat(item.raw_value);
                if (transform !== undefined) {
                    y = transform(y);
                }
                return {
                    y,
                    x: itemToX(item),
                    job_id: item.job_id,
                    step_index: item.step_index,
                    tooltip: "click to view job",
                };
            });
            return {
                x_title: attributeToLabel[groupBy.value],
                y_title: yTitle,
                helpTerm,
                values,
            };
        }
        function metricToAggregateData(jobMetrics, metricName, yTitle, helpTerm, transform) {
            const thisMetric = jobMetrics?.filter((jobMetric) => jobMetric.name == metricName);
            const aggregateByX = {};
            thisMetric?.forEach((item) => {
                let y = parseFloat(item.raw_value);
                if (transform !== undefined) {
                    y = transform(y);
                }
                const x = itemToX(item);
                if (!(x in aggregateByX)) {
                    aggregateByX[x] = 0;
                }
                if (x in aggregateByX) {
                    aggregateByX[x] += y;
                }
            });
            const values = Object.keys(aggregateByX).map((key) => {
                const value = aggregateByX[key];
                return {
                    category: key,
                    y: value || 0,
                };
            });
            return {
                category_title: attributeToLabel[groupBy.value],
                y_title: yTitle,
                helpTerm,
                values,
            };
        }
        function transformTime(time) {
            if (timing.value == "minutes") {
                return time / 60;
            }
            else if (timing.value == "hours") {
                return time / 3600;
            }
            else {
                return time;
            }
        }
        const allocatedCoreTime = computed(() => {
            return computeAllocatedCoreTime(jobMetrics.value);
        });
        const wallclock = computed(() => {
            const title = `Runtime (in ${timingInTitles.value})`;
            return metricToSpecData(jobMetrics.value, "runtime_seconds", title, "galaxy.jobs.metrics.walltime", transformTime);
        });
        const wallclockAggregate = computed(() => {
            const title = `Runtime (in ${timingInTitles.value})`;
            return metricToAggregateData(jobMetrics.value, "runtime_seconds", title, "galaxy.jobs.metrics.walltime", transformTime);
        });
        const allocatedCoreTimeSpec = computed(() => {
            const title = `Allocated Core Time (in ${timingInTitles.value})`;
            return metricToSpecData(allocatedCoreTime.value, "allocated_core_time", title, "galaxy.jobs.metrics.allocated_core_time", transformTime);
        });
        const allocatedCoreTimeAggregate = computed(() => {
            const title = `Allocated Core Time (in ${timingInTitles.value})`;
            return metricToAggregateData(allocatedCoreTime.value, "allocated_core_time", title, "galaxy.jobs.metrics.allocated_core_time", transformTime);
        });
        const coresAllocated = computed(() => {
            return metricToSpecData(jobMetrics.value, "galaxy_slots", "Cores Allocated", "galaxy.jobs.metrics.cores");
        });
        const memoryAllocated = computed(() => {
            return metricToSpecData(jobMetrics.value, "galaxy_memory_mb", "Memory Allocated (in MB)");
        });
        const peakMemory = computed(() => {
            return metricToSpecData(jobMetrics.value, "memory.peak", "Max memory usage recorded (in MB)", undefined, (v) => v / 1024 ** 2);
        });
        function itemToBarChartSpec(item) {
            const spec = {
                $schema: "https://vega.github.io/schema/vega-lite/v5.json",
                description: "Aggregate data.",
                data: {
                    values: item.values,
                },
                mark: { type: "bar", tooltip: true },
                encoding: {
                    order: { field: "y", type: "quantitative", sort: "descending" },
                    y: { field: "y", type: "quantitative", title: item.y_title },
                    color: {
                        field: "category",
                        type: "nominal",
                        sort: { field: "y", order: "descending" },
                        legend: {
                            type: "symbol",
                            title: item.category_title,
                            labelExpr: "truncate(replace(datum.label, /(^\\d+: )?.*\\/repos\\/[^/]+\\/[^/]+\\//, '$1'), 32)",
                        },
                    },
                    tooltip: [
                        { field: "category", type: "nominal", title: item.category_title },
                        { field: "y", type: "quantitative", title: item.y_title },
                    ],
                },
                autosize: {
                    type: "fit",
                    resize: true,
                },
                width: 400,
                height: 250,
            };
            return spec;
        }
        function itemToSpec(item) {
            const spec = {
                $schema: "https://vega.github.io/schema/vega-lite/v5.json",
                description: "A boxplot with jittered points.",
                data: {
                    values: item.values,
                },
                transform: [
                    {
                        calculate: "random() - 0.5",
                        as: "random_jitter",
                    },
                    {
                        calculate: "'" + getAppRoot() + "jobs/' + datum.job_id + '/view'",
                        as: "url",
                    },
                    {
                        calculate: "parseInt(datum.step_index)",
                        as: "x_numeric",
                    },
                ],
                encoding: {
                    y: {
                        field: "y",
                        type: "quantitative",
                        scale: { zero: false },
                        title: item.y_title,
                    },
                    x: {
                        field: "x",
                        type: "ordinal",
                        title: item.x_title,
                        axis: {
                            labelAngle: -45,
                            labelAlign: "right",
                            // 35 seems to be the maximum amount of characters we can display
                            labelExpr: "truncate(replace(datum.label, /(^\\d+: )?.*\\/repos\\/[^/]+\\/[^/]+\\//, '$1'), 35)",
                        },
                        sort: {
                            field: "step_index",
                        },
                    },
                },
                layer: [
                    {
                        mark: { type: "boxplot", opacity: 0.5 },
                        width: "container",
                    },
                    {
                        mark: {
                            type: "point",
                            opacity: 0.7,
                        },
                        encoding: {
                            xOffset: { field: "random_jitter", type: "quantitative", scale: { domain: [-2, 2] } },
                            tooltip: { field: "tooltip", type: "nominal" },
                            href: { field: "url", type: "nominal" },
                            color: { field: "x", type: "nominal", legend: null },
                        },
                        width: "container",
                        height: 350,
                    },
                ],
            };
            return spec;
        }
        const metrics = computed(() => {
            const items = [
                wallclock.value,
                allocatedCoreTimeSpec.value,
                coresAllocated.value,
                memoryAllocated.value,
                peakMemory.value,
            ].filter((item) => item.values?.length);
            return Object.fromEntries(items.map((item) => [item.y_title, { spec: itemToSpec(item), item: item }]));
        });
        const timingInTitles = computed(() => {
            return capitalizeFirstLetter(timing.value);
        });
        const groupByInTitles = computed(() => {
            return attributeToLabel[groupBy.value];
        });
        return { __sfc: true, VegaWrapper, props, groupBy, timing, jobMetrics, fetchError, attributeToLabel, fetchMetrics, itemToX, computeAllocatedCoreTime, metricToSpecData, metricToAggregateData, transformTime, allocatedCoreTime, wallclock, wallclockAggregate, allocatedCoreTimeSpec, allocatedCoreTimeAggregate, coresAllocated, memoryAllocated, peakMemory, itemToBarChartSpec, itemToSpec, metrics, timingInTitles, groupByInTitles, BAlert, BButtonGroup, BCol, BContainer, BRow, capitalizeFirstLetter, LoadingSpan, HelpText };
    }
});
